import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { useStore } from '@tanstack/react-form';
import debounce from 'lodash.debounce';
import { FormDatePicker, FormSwitch } from '../FormInputs';
import { QueryInput } from '../../pages/NarrativeAddPage/QueryInput/QueryInput';
import { NearMixedPairWarning } from '../NearMixedPairWarning/NearMixedPairWarning';
import { keywordsNativeLanguageOptions } from '../../utils/languages';
import { useCurrentUser } from '../../contexts/CurrentUser';


interface SearchGroupProps {
  form: any;
  isCase?: boolean;
  quillRef?: React.RefObject<any>;
  isDisabled?: boolean;
}

export const SearchGroup: React.FC<SearchGroupProps> = ({ form, isCase, quillRef, isDisabled = false }) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const keywordsRef = useRef('');
  const [isValidQuery, setIsValidQuery] = useState<boolean | null>(null);
  const [hasMixedNearPair, setHasMixedNearPair] = useState(false);
  const validationRef = useRef<boolean | null>(isValidQuery);

  const workspaceConfig = currentUser?.workspace?.config;
  const isAdvancedSyntax =
    workspaceConfig?.keywords_parser_class === 'Workspace16ESKeywordsQuery';

  const startDate = useStore(form.store, (state: any) => state.values.startDate);
  const isMultilang = useStore(form.store, (state: any) => state.values.isMultilang);
  const disableFilters = form.state.values.disableFilters;
  
  useEffect(() => {
    if (isMultilang && form.state.values.nativeLang === undefined) {
      form.setFieldValue('nativeLang', { value: '', label: 'Detect Automatically' });
    } else if (!isMultilang && form.state.values.nativeLang !== undefined) {
      form.setFieldValue('nativeLang', undefined);
    }
  }, [isMultilang, form]);

  const debouncedSetFieldValue = useMemo(
    () =>
      debounce((value: string) => {
        form.setFieldValue('keywords', value);
      }, 300),
    [form],
  );

  const onKeywordsChange = useCallback(
    (value: string, isValid: boolean | null) => {
      keywordsRef.current = value;
      if(isValid !== null) {
      setIsValidQuery(isValid);
    } else if (isValid === null && !validationRef.current) {
      setIsValidQuery(true);
    }
      debouncedSetFieldValue(value);
    },
    [debouncedSetFieldValue],
  );

  useEffect(() => {
    return () => {
      debouncedSetFieldValue.cancel();
    };
  }, [debouncedSetFieldValue]);

  const languageOptions = keywordsNativeLanguageOptions.map((option) => ({
    value: option.value,
    label: t(option.label),
  }));

  return (
    <>
      <div className="group-card">
        <form.Field
          name='keywords'
          validators={{
            onChange: () => {
              if (isValidQuery === false) {
                return t('Invalid search query');
              }
              return undefined;
            },
            onBlur: () => {
              if (isValidQuery === false) {
                return t('Invalid search query');
              }
              return undefined;
            },
          }}
          >
            <div className="form-element" data-field="keywords">
              <label className="label">{t('Keywords')}</label>
              <QueryInput
                isAdvanced={isAdvancedSyntax}
                onChange={onKeywordsChange}
                isInvalid={false}
                disabled={isDisabled || disableFilters}
                keywords={form.state.values.keywords || ''}
                ref={quillRef}
                onMixedNearPairChange={setHasMixedNearPair}
              />
            </div>

          </form.Field>
        

        <div className="group-card-row">
          <form.Field
            name="isCaseOpen"
            children={(field: any) => (
              <FormDatePicker
                form={form}
                startDateName="startDate"
                endDateName="endDate"
                label={field.state.value ? t('Start date') : t('Date range')}
                isRange={!field.state.value}
                isCase={isCase}
                required={true}
                disabled={isDisabled}
              />
            )}
          />

          <div className="form-element-group">
            <FormSwitch
              form={form}
              name="isMultilang"
              label={t('Multilingual search')}
              subLabel={t('Search for relevant messages in other languages')}
              disabled={isDisabled}
            />

            <form.Field
              name="isMultilang"
              children={(field: any) => (
                field.state.value && (
                  <form.Field
                    name="nativeLang"
                    validators={{
                      onBlur: ({ value }: any) => {
                        const isMultilang = form.getFieldValue('isMultilang');
                        if (isMultilang === true && value === null) {
                          return t('You must select a native language for multilanguage search');
                        }
                        return undefined;
                      },
                    }}
                    children={(nativeLangField: any) => {
                      const hasError = nativeLangField.state.meta.errors?.length > 0;
                      const errorMessage = nativeLangField.state.meta.errors?.[0];
                      
                      return (
                        <div className={hasError ? 'form-element error' : 'form-element'} id="nativeLang">
                          <label htmlFor="nativeLang">
                            {t('Select native language')}*
                          </label>
                          <Select
                            id="nativeLang"
                            name="nativeLang"
                            value={nativeLangField.state.value}
                            onChange={(selectedOption) => {
                              nativeLangField.handleChange(selectedOption);
                            }}
                            onBlur={nativeLangField.handleBlur}
                            options={languageOptions}
                            placeholder={t('Language')}
                            isClearable
                            isDisabled={isDisabled}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                          {hasError && <div className="error-message">{errorMessage}</div>}
                        </div>
                      );
                    }}
                  />
                )
              )}
            />
          </div>
        </div>

        {hasMixedNearPair ? (
          <div className="form-element">
            <NearMixedPairWarning startDate={startDate} isAdvanced={isAdvancedSyntax} />
          </div>
        ) : null}
      </div>
    </>
  );
};
